import { notifyError, notifySuccess } from '@/core/notify/notify';
import { phase1Service } from '@/core/services/phase1';
import { practiceService } from '@/core/services/practice';
import { rememberChosenPeriod, resolveDefaultPeriodId } from '@/core/periodDefault';
import type {
  MappingCheckReport,
  MappingOverrideInput,
  MappingWorkspaceQuery,
  MappingWorkspaceRow,
  MappingWorkspaceSummary,
  ReviewStatus,
  SaveMappingsOptions,
} from '@/core/models/phase1.models';
import type { PeriodRow } from '@/core/models/practice.models';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { formatNatural } from '@/core/format/naturalBalance';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { InfoHint } from '@/kit/InfoHint';
import './TaxreturnNominalmapping.scss';
import { Select } from '@/kit/Select';
import { SelectionBar } from '@/kit/SelectionBar';
import { openDialog } from '@/kit/dialog';
import { MappingWarningsDialog } from './MappingDialogs';
import {
  ChangeInput,
  ExportMenu,
  SearchField,
  type ReportingCodeOption,
  flattenReportingCodes,
  periodDash,
  useAlive,
  useClientInit,
  useLive,
} from './common';

const ALL = -1;

const REVIEWED_OPTIONS = [
  { id: ALL, name: 'All' },
  { id: 0, name: 'Not reviewed' },
  { id: 1, name: 'Reviewed' },
  { id: 2, name: 'Queried' },
];

interface Model {
  dataSource: MappingWorkspaceRow[];
  selectedIds: Set<string>;
  periods: PeriodRow[];
  selectedPeriodId: string | null;
  reportingCodes: ReportingCodeOption[];
  summary: MappingWorkspaceSummary | null;
  /** APT335: the mapping check of the period (ChartMapping/consistency): null until it has loaded, or when it cannot be read. */
  check: MappingCheckReport | null;
  checkOpen: boolean;
  /** APT335: every postable code in the row selects, not only the account's own (the server still blocks and warns). */
  showAllCodes: boolean;
  searchText: string;
  unmappedOnly: boolean;
  materialOnly: boolean;
  reviewedFilter: number;
  /** Target code for the bulk-assign action. */
  bulkReportingCodeId: string | null;
  /**
   * Rows edited since the last load; nothing autosaves. APT249: kept in state (it was a ref) so an edited row shows
   * "Unsaved" and the header counts the pending changes - a code chosen in a row is only stored by Save (or Apply).
   */
  dirtyIds: Set<string>;
  /** Save or Apply Suggested is running (stops a second click). */
  busy: boolean;
}

const APPLY_NEEDS_SELECTION = 'Tick the nominals to map first.';
/** The reserve reporting codes (equity, other than share capital): they group an account in the Adjusted Trial Balance and the working papers. */
const RESERVE_CODES = new Set(['EQ-PL', 'EQ-SPREM', 'EQ-REVAL', 'EQ-CRR', 'EQ-FV', 'EQ-OTHER', 'EQ-OCI']);

/** What is true of it (APT335, 1 Nov): the accounts print each account on the line of its account type and name, from the Adjusted Trial Balance and from the Source TB alike. */
const RESERVE_INFO =
  'This code groups the reserve in the Adjusted Trial Balance and the working papers. The reserve line it prints on in the accounts (profit and loss, special, general, revaluation, capital redemption and so on) follows the account type and name, the same for accounts made from the Adjusted Trial Balance or the Source TB, so changing the code here does not move it.';

const APPLY_NEEDS_CODE = "Choose a reporting code in 'Bulk map selected to...', or in the ticked rows, first.";
const APPLY_SAVES_ROWS = 'Save the reporting codes chosen in the ticked rows.';
const APPLY_BULK = 'Point the ticked nominals at the chosen code; Save stores it.';

/** APT249: the ticked rows that carry an unsaved change (e.g. a reporting code chosen in the row). */
const tickedUnsaved = (s: Model) => [...s.selectedIds].filter((id) => s.dirtyIds.has(id));

/** APT335: the problem the API returned, as the HTTP client keeps it (`error.error`: RFC 7807 with `code`, `detail` and the per-account `errors`). */
interface MappingProblem {
  code?: string;
  detail?: string;
  errors?: { field?: string; code?: string; message?: string }[];
}
const problemOf = (error: unknown): MappingProblem =>
  ((error as { error?: MappingProblem } | null)?.error ?? {}) as MappingProblem;
const apiErrorCode = (error: unknown) => problemOf(error).code;
const apiErrorMessages = (error: unknown) => {
  const problem = problemOf(error);
  const messages = (problem.errors ?? []).map((e) => e.message).filter((x): x is string => !!x);
  return messages.length > 0 ? messages : problem.detail ? [problem.detail] : [];
};

/**
 * AP-004 mapping screen: reporting-code tree, suggested and bulk mapping, description overrides, reviewed status,
 * unmapped/material filters and multi-year balances. Every row carries the version it was loaded at, so a save cannot
 * silently overwrite someone else's change (test AP-CON-001). (Angular TaxreturnNominalmappingComponent.)
 */
export function TaxreturnNominalmapping() {
  const alive = useAlive();
  const [m, set, ref] = useLive<Model>({
    dataSource: [],
    selectedIds: new Set<string>(),
    periods: [],
    selectedPeriodId: null,
    reportingCodes: [],
    summary: null,
    check: null,
    checkOpen: false,
    showAllCodes: false,
    searchText: '',
    unmappedOnly: false,
    materialOnly: false,
    reviewedFilter: ALL,
    bulkReportingCodeId: null,
    dirtyIds: new Set<string>(),
    busy: false,
  });

  const markDirty = (...ids: string[]) => {
    set({ dirtyIds: new Set([...ref.current.dirtyIds, ...ids]) });
  };

  const query = (): MappingWorkspaceQuery => {
    const s = ref.current;
    return {
      unmappedOnly: s.unmappedOnly,
      materialOnly: s.materialOnly,
      reviewedStatus: s.reviewedFilter === ALL ? null : (s.reviewedFilter as ReviewStatus),
      statementArea: null,
      search: s.searchText?.trim() || null,
      sortBy: 'code',
      sortDescending: false,
      pageNumber: 1,
      pageSize: 500,
    };
  };

  const load = async () => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      return;
    }
    try {
      const page = await phase1Service.getMappingWorkspace(periodId, query());
      if (!alive.current) return;
      const dataSource = page.items ?? [];
      const ids = new Set(dataSource.map((r) => r.nominalAccountId));
      set({
        dataSource,
        summary: page.summary,
        selectedIds: new Set([...ref.current.selectedIds].filter((id) => ids.has(id))),
        dirtyIds: new Set<string>(),
      });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  /**
   * APT335: the mapping check of the period, shown in the header (it was an API the screen never called). A failure is not toasted: the check is
   * information, and the screen works without it.
   */
  const loadCheck = async () => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      return;
    }
    try {
      const report = await phase1Service.getMappingCheck(periodId);
      if (alive.current) set({ check: report ?? null });
    } catch {
      if (alive.current) set({ check: null });
    }
  };

  const loadReportingCodes = async () => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      return;
    }
    try {
      const tree = await phase1Service.getReportingCodes(periodId);
      if (alive.current) set({ reportingCodes: flattenReportingCodes(tree) });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  useClientInit(async (client) => {
    try {
      const response = await practiceService.getClientPeriods(client.id);
      if (!alive.current) return;
      const periods = response.periods ?? [];
      // The period the user last chose, else the latest with data, else the latest that has ended (see pickDefaultPeriodId).
      const selectedPeriodId = await resolveDefaultPeriodId(client.id, periods);
      set({ periods, selectedPeriodId });
      if (selectedPeriodId) {
        void loadReportingCodes();
        void load();
        void loadCheck();
      } else {
        notifyError('Add an accounting period before mapping nominals.');
      }
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  });

  const onPeriodChange = (periodId: string | null) => {
    rememberChosenPeriod(periodId);
    set({
      selectedPeriodId: periodId,
      selectedIds: new Set<string>(),
      dirtyIds: new Set<string>(),
      check: null,
      checkOpen: false,
    });
    void loadReportingCodes();
    void load();
    void loadCheck();
  };

  /**
   * APT335: the codes offered for a row follow the account's nature - the API names them (its section and side, its own type's codes first); with no
   * chart account to judge by, or "Show all codes", every postable code. A saved code outside the offer is still in the list (never dropped) so
   * the row shows what it holds.
   */
  const optionsFor = (row: MappingWorkspaceRow): ReportingCodeOption[] => {
    const all = m.reportingCodes;
    const allowed = row.allowedReportingCodes;
    if ((m.showAllCodes && !row.isLocked) || !allowed) {
      return all;
    }
    const wanted = new Set(allowed.map((c) => c.toLowerCase()));
    const offered = all.filter((o) => o.code !== undefined && wanted.has(o.code.toLowerCase()));
    if (row.reportingCodeId && !offered.some((o) => o.id === row.reportingCodeId)) {
      const current = all.find((o) => o.id === row.reportingCodeId);
      if (current) offered.push(current);
    }
    return offered;
  };

  /**
   * APT335: "Bulk map selected to..." offers the codes on offer to every ticked account that can be mapped (each one's own list, as above), so ticking
   * balance sheet accounts no longer offers profit and loss codes. Every code while nothing mappable is ticked (a controlled account keeps its mapping).
   * Ticked accounts of different kinds share no code: the list is then empty and says so.
   */
  const bulkOptions = ((): ReportingCodeOption[] => {
    const mappable = m.dataSource.filter((row) => m.selectedIds.has(row.nominalAccountId) && !row.isLocked);
    if (mappable.length === 0) {
      return m.reportingCodes;
    }
    const offered = mappable.map((row) => new Set(optionsFor(row).map((o) => o.id)));
    return m.reportingCodes.filter((o) => offered.every((ids) => ids.has(o.id)));
  })();
  /** The chosen bulk code, unless the ticked accounts no longer offer it (it is then not applied). */
  const bulkCodeId = bulkOptions.some((o) => o.id === m.bulkReportingCodeId) ? m.bulkReportingCodeId : null;

  const checkErrors = m.check?.issues.filter((i) => i.severity === 'Error').length ?? 0;
  const checkWarnings = (m.check?.issues.length ?? 0) - checkErrors;
  const isAllSelected = m.dataSource.length > 0 && m.dataSource.every((r) => m.selectedIds.has(r.nominalAccountId));
  const isSomeSelected = m.selectedIds.size > 0 && !isAllSelected;
  /** APT249: why Apply has nothing to do yet (shown as its title while disabled), or null when it can run. */
  const applyBlockedBy =
    m.selectedIds.size === 0
      ? APPLY_NEEDS_SELECTION
      : !bulkCodeId && tickedUnsaved(m).length === 0
        ? APPLY_NEEDS_CODE
        : null;

  const toggleAll = (checked: boolean) => {
    set({ selectedIds: checked ? new Set(ref.current.dataSource.map((r) => r.nominalAccountId)) : new Set<string>() });
  };

  const toggleRow = (row: MappingWorkspaceRow, checked: boolean) => {
    const selectedIds = new Set(ref.current.selectedIds);
    if (checked) {
      selectedIds.add(row.nominalAccountId);
    } else {
      selectedIds.delete(row.nominalAccountId);
    }
    set({ selectedIds });
  };

  /** Row edits mutate the loaded row in Angular; here the row is replaced and marked dirty in one step. */
  const editRow = (row: MappingWorkspaceRow, changes: Partial<MappingWorkspaceRow>) => {
    set({
      dataSource: ref.current.dataSource.map((r) =>
        r.nominalAccountId === row.nominalAccountId ? { ...r, ...changes } : r,
      ),
    });
  };

  const onRowChanged = (row: MappingWorkspaceRow) => {
    markDirty(row.nominalAccountId);
  };

  const onReviewedChanged = (row: MappingWorkspaceRow, checked: boolean) => {
    editRow(row, { reviewedStatus: checked ? 1 : 0 });
    markDirty(row.nominalAccountId);
  };

  /**
   * APT249: "Apply" maps the ticked nominals. With a code in "Bulk map selected to..." it points every ticked row at
   * that code (pending, like any row edit, until Save). With no bulk code, the ticked rows already carry the codes
   * chosen in their own Reporting code select, so Apply stores them - it used to stop with "Select the reporting code
   * to assign." though the user had picked the code in the row. The button is disabled, with a title saying what is
   * missing, while there is nothing to apply.
   */
  const onBulkAssign = () => {
    const s = ref.current;
    if (s.selectedIds.size === 0) {
      notifyError(APPLY_NEEDS_SELECTION);
      return;
    }
    if (!bulkCodeId) {
      if (tickedUnsaved(s).length === 0) {
        notifyError(APPLY_NEEDS_CODE);
        return;
      }
      // Every pending change is saved, not only the ticked rows': the reload after a save would otherwise drop them.
      void onSave();
      return;
    }
    // APT335: a system mapping is controlled - the ticked rows that carry one keep it (the server refuses them anyway).
    const mappable = s.dataSource.filter((row) => s.selectedIds.has(row.nominalAccountId) && !row.isLocked);
    const skipped = s.selectedIds.size - mappable.length;
    const ids = new Set(mappable.map((row) => row.nominalAccountId));
    set({
      dataSource: s.dataSource.map((row) =>
        ids.has(row.nominalAccountId) ? { ...row, reportingCodeId: bulkCodeId } : row,
      ),
    });
    markDirty(...ids);
    if (skipped > 0) {
      notifyError(
        `${skipped} ticked account(s) have a system mapping, which is controlled, so they were left as they are.`,
      );
    }
  };

  /** Accepts a single suggestion into the row's reporting code. */
  const onAcceptSuggestion = (row: MappingWorkspaceRow) => {
    if (!row.suggestedReportingCodeId) {
      return;
    }
    editRow(row, { reportingCodeId: row.suggestedReportingCodeId });
    markDirty(row.nominalAccountId);
  };

  /** Accepts every outstanding suggestion server-side, then reloads. */
  const onApplyAllSuggestions = async () => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (ref.current.busy) {
      return;
    }
    set({ busy: true });
    try {
      const result = await phase1Service.applySuggestions(periodId);
      if (!alive.current) return;
      // APT335: a suggestion that does not fit the account (another section, or the other side by nature) is left for the user.
      const left = result.skipped
        ? ` ${result.skipped} left for you to decide - their suggested code does not fit the account.`
        : '';
      notifySuccess(`${result.applied} suggested mapping(s) applied.${left}`);
      void load();
      void loadCheck();
    } catch (error) {
      if (alive.current) notifyError(error);
    } finally {
      if (alive.current) set({ busy: false });
    }
  };

  const onSave = async () => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    const changed = ref.current.dataSource.filter((r) => ref.current.dirtyIds.has(r.nominalAccountId));
    if (changed.length === 0) {
      notifyError('There are no mapping changes to save.');
      return;
    }

    const mappings: MappingOverrideInput[] = changed.map((r) => ({
      nominalAccountId: r.nominalAccountId,
      reportingCodeId: r.reportingCodeId,
      mappingSource: 1,
      reviewedStatus: r.reviewedStatus,
      displayDescription: r.displayDescription,
      expectedETag: r.eTag,
    }));

    if (ref.current.busy) {
      return;
    }
    set({ busy: true });
    try {
      await saveMappings(periodId, mappings, {});
    } finally {
      if (alive.current) set({ busy: false });
    }
  };

  /**
   * APT335: the save, with what the API may ask first. A code on the other side of the account's nature (AP-MAP-005) is shown and saved only when the
   * user confirms. A code of another section (AP-MAP-003) and a system mapping (AP-MAP-004: nobody may change one, the client's answer of 28 Oct) are
   * shown as they are and keep the edits.
   */
  const saveMappings = async (
    periodId: string,
    mappings: MappingOverrideInput[],
    options: SaveMappingsOptions,
  ): Promise<void> => {
    try {
      // The options go down only once the API has asked for them (a first attempt is the plain save).
      const result = options.confirmWarnings
        ? await phase1Service.saveMappings(periodId, mappings, options)
        : await phase1Service.saveMappings(periodId, mappings);
      if (!alive.current) return;
      notifySuccess(`${result.applied} mapping(s) saved, ${result.cleared} cleared.`);
      void load();
      void loadCheck();
    } catch (error) {
      if (!alive.current) return;
      const code = apiErrorCode(error);
      if (code === 'AP-MAP-005') {
        const confirmed = await openDialog<{ messages: string[] }, boolean>(MappingWarningsDialog, {
          panelClass: 'cst-invite',
          data: { messages: apiErrorMessages(error) },
        }).afterClosed();
        if (confirmed === true && alive.current)
          await saveMappings(periodId, mappings, { ...options, confirmWarnings: true });
        return;
      }
      notifyError(error);
    }
  };

  /** format: 1 CSV, 2 Excel, 3 PDF. */
  const onExport = async (format: number) => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    try {
      await phase1Service.exportMappings(periodId, query(), { format, isPrint: false });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const columns: DataTableColumn<MappingWorkspaceRow>[] = [
    {
      id: 'select',
      header: (
        <Checkbox
          checked={isAllSelected}
          indeterminate={isSomeSelected}
          onChange={(event) => toggleAll(event.target.checked)}
        />
      ),
      cell: (element) => (
        <Checkbox
          checked={m.selectedIds.has(element.nominalAccountId)}
          onChange={(event) => toggleRow(element, event.target.checked)}
        />
      ),
    },
    {
      id: 'code',
      header: 'Nominal',
      cell: (element) => <span className={element.isMaterial ? 'fw-bold' : ''}>{element.nominalCode}</span>,
    },
    { id: 'description', header: 'Description', cell: (element) => element.nominalDescription },
    {
      id: 'display',
      header: 'Display description',
      cell: (element) => (
        <ChangeInput
          fieldClassName="w-200"
          placeholder="Override"
          charMode="text"
          value={element.displayDescription ?? ''}
          onValueChange={(displayDescription) => editRow(element, { displayDescription })}
          onCommit={() => onRowChanged(element)}
        />
      ),
    },
    {
      id: 'reportingCode',
      header: 'Reporting code',
      cell: (element) => (
        <>
          {/* APT335: grouped by statement section, wide enough for the labels, narrowed by the account's nature; a system mapping is read-only. */}
          <span className="nm-code-line">
            <Select
              className="w-250"
              panelClass="myPanelClass"
              panelMinWidth={480}
              groupBy="group"
              placeholder="Unmapped"
              items={optionsFor(element)}
              bindLabel="name"
              bindValue="id"
              value={element.reportingCodeId}
              disabled={!!element.isLocked}
              clearable={!element.isLocked}
              onChange={(value) => {
                editRow(element, { reportingCodeId: (value as string | null) ?? null });
                onRowChanged(element);
              }}
            />
            {/* APT368 (client): the repeated "System mapping - controlled" text under every locked row is one small info icon at the right of the box; the text is in its tooltip. */}
            {element.isLocked && (
              <span className="nm-lock-hint">
                <InfoHint
                  text={`${element.lockReason ?? 'System mapping - controlled'}. The reporting code follows the account type of the standard chart and cannot be changed.`}
                  placement="left"
                />
              </span>
            )}
          </span>
          {/* APT335 (client, 1 Nov): "just adding an info icon is enough next to the reporting code" - the reserve a code groups does not change where the account prints. */}
          {RESERVE_CODES.has((element.reportingCode ?? '').toUpperCase()) && (
            <span className="reserve-info" role="img" aria-label={RESERVE_INFO} title={RESERVE_INFO}>
              {' '}
              <Icon name="info" outlined className="font-size-16 text-primary" />
            </span>
          )}
          {element.mappedOutsideAllowed && (
            <span
              className="text-red"
              title="This code is not one of the codes offered for this account (its section and side)."
            >
              {' '}
              Outside the usual codes
            </span>
          )}
          {!element.reportingCodeId && element.suggestedReportingCode && (
            <a className="link" onClick={() => onAcceptSuggestion(element)}>
              Suggested: {element.suggestedReportingCode}
            </a>
          )}
          {/* APT249: an edited row says so until Save stores it. */}
          {m.dirtyIds.has(element.nominalAccountId) && (
            <span className="text-muted" title="Changed - click Save to keep it">
              {' '}
              Unsaved
            </span>
          )}
        </>
      ),
    },
    // APT349: each nominal's amounts are shown on its natural side - positive without a sign where it normally lies, in brackets on the other side. Since the
    // follow-up the API sends the side of the nominal's own category (account type, contra accounts such as accumulated depreciation credit), so an unmapped
    // nominal is shown by its nature too; only a nominal with no nature at all (suspense, no chart account, no code) keeps its stored sign, a negative in
    // brackets. The stored amounts are unchanged.
    { id: 'source', header: 'Source', cell: (element) => formatNatural(element.sourceAmount, element.normalSign) },
    { id: 'journals', header: 'Journals', cell: (element) => formatNatural(element.journalAmount, element.normalSign) },
    {
      id: 'adjusted',
      header: 'Adjusted',
      cell: (element) => formatNatural(element.adjustedAmount, element.normalSign),
    },
    {
      id: 'prioryear',
      header: 'Prior year',
      cell: (element) => formatNatural(element.priorYearAmount, element.normalSign),
    },
    {
      id: 'reviewed',
      header: 'Reviewed',
      cell: (element) => (
        <Checkbox
          checked={element.reviewedStatus === 1}
          onChange={(event) => onReviewedChanged(element, event.target.checked)}
        />
      ),
    },
  ];

  return (
    <SideMenuPage menu="task">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              placeholder="Accounting period"
              items={m.periods}
              bindLabel="fromDate"
              bindValue="id"
              clearable={false}
              value={m.selectedPeriodId}
              onChange={(value) => onPeriodChange((value as string | null) ?? null)}
              renderLabel={periodDash}
              renderOption={periodDash}
            />

            <SearchField
              value={m.searchText}
              onValueChange={(searchText) => set({ searchText })}
              onCommit={() => void load()}
            />

            <Checkbox
              checked={m.unmappedOnly}
              onChange={(event) => {
                set({ unmappedOnly: event.target.checked });
                void load();
              }}
            >
              Unmapped only
            </Checkbox>
            <Checkbox
              checked={m.materialOnly}
              onChange={(event) => {
                set({ materialOnly: event.target.checked });
                void load();
              }}
            >
              Material only
            </Checkbox>
            <Checkbox checked={m.showAllCodes} onChange={(event) => set({ showAllCodes: event.target.checked })}>
              Show all codes
            </Checkbox>
          </div>

          <div className="content-header-right mb-1">
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              items={REVIEWED_OPTIONS}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={m.reviewedFilter}
              onChange={(value) => {
                set({ reviewedFilter: value as number });
                void load();
              }}
            />
            <Button className="action-button secondary-button" busy={m.busy} onClick={() => void onApplyAllSuggestions()}>
              <Icon name="auto_fix_high" outlined />
              <span>Apply Suggested</span>
            </Button>
            <Button className="action-button primary-button" busy={m.busy} onClick={() => void onSave()}>
              <Icon name="save" outlined />
              <span>Save</span>
            </Button>
            <ExportMenu onExport={(format) => void onExport(format)} />
          </div>
        </div>

        <div className="content-header mb-0">
          {m.summary && (
            <div className="content-header-left mb-1">
              <span className={m.summary.unmappedMaterialCount > 0 ? 'text-red' : 'text-green'}>
                {m.summary.unmappedCount} unmapped ({m.summary.unmappedMaterialCount} material)
              </span>
              <span>
                &nbsp;&middot;&nbsp;{m.summary.reviewedCount} of {m.summary.totalNominals} reviewed
              </span>
              {m.dirtyIds.size > 0 && (
                <span className="text-red">
                  &nbsp;&middot;&nbsp;{m.dirtyIds.size} unsaved change(s) - click Save to keep them
                </span>
              )}
              {m.check && (
                <>
                  <span>&nbsp;&middot;&nbsp;</span>
                  {m.check.issues.length === 0 ? (
                    <span className="text-green">Mapping check: no problems</span>
                  ) : (
                    <a
                      className={`link ${checkErrors > 0 ? 'text-red' : ''}`}
                      onClick={() => set({ checkOpen: !m.checkOpen })}
                    >
                      Mapping check: {checkErrors} error(s), {checkWarnings} warning(s)
                    </a>
                  )}
                </>
              )}
            </div>
          )}
          <div className="content-header-right mb-1">
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              placeholder="Bulk map selected to..."
              panelMinWidth={480}
              groupBy="group"
              items={bulkOptions}
              notFoundText="No code fits all the ticked accounts - tick accounts of one kind"
              bindLabel="name"
              bindValue="id"
              value={bulkCodeId}
              onChange={(value) => set({ bulkReportingCodeId: (value as string | null) ?? null })}
            />
            <Button
              className="action-button secondary-button"
              disabled={!!applyBlockedBy}
              title={applyBlockedBy ?? (bulkCodeId ? APPLY_BULK : APPLY_SAVES_ROWS)}
              onClick={onBulkAssign}
            >
              <Icon name="playlist_add_check" outlined />
              <span>Apply</span>
            </Button>
          </div>
        </div>

        {m.checkOpen && m.check && m.check.issues.length > 0 && (
          <ul className="mapping-check-list" aria-label="Mapping check">
            {m.check.issues.slice(0, 50).map((issue, index) => (
              <li key={index} className={issue.severity === 'Error' ? 'text-red' : ''}>
                {issue.severity}: {issue.message}
              </li>
            ))}
            {m.check.issues.length > 50 && <li className="text-muted">...and {m.check.issues.length - 50} more.</li>}
          </ul>
        )}

        {/* Mappings are not deleted or exported one by one: the ticked nominals take "Bulk map selected to..." above. */}
        <SelectionBar count={m.selectedIds.size} onClear={() => toggleAll(false)} />
        <div className="mat-table-wrapper">
          <DataTable columns={columns} rows={m.dataSource} rowKey={(row) => row.nominalAccountId} stickyHeader />
        </div>
      </div>
    </SideMenuPage>
  );
}
